import React from 'react';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import { useFilterField } from '../../hooks/useFilterField';
import { useFilterBarContext } from '../../state/FilterBarContext';
import type { DateRangeFilterConfig, DateRangeValue } from '../../types';

const { RangePicker } = DatePicker;

export interface DateRangeFilterControlProps {
  name: string;
  /** When set, becomes the RangePicker placeholder for both bounds (formatted as
   *  `${label} from` / `${label} to`). Overrides config.placeholder. */
  placeholder?: string;
}

/**
 * New wrapper over antd `DatePicker.RangePicker`.
 *
 * Plan Section "Recommended Approach":
 *   "DateRangeFilterControl — wraps antd DatePicker.RangePicker in the same
 *    label/error shell as the existing Datepicker."
 *
 * Emits canonical range values: `null` for unset, `[a, null]` / `[null, b]`
 * for partial bounds, `[a, b]` for fully bounded.
 */
export const DateRangeFilterControl: React.FC<DateRangeFilterControlProps> = ({ name, placeholder: placeholderOverride }) => {
  const { value, setValue, config } = useFilterField(name);
  const { size } = useFilterBarContext();

  if (!config || config.type !== 'DATE_RANGE') return null;
  const dr = config as DateRangeFilterConfig;
  const size_ = dr.size ?? size;

  const currentValue = (value as DateRangeValue) ?? [null, null];

  const handleChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates || (dates[0] === null && dates[1] === null)) {
      setValue(null);
      return;
    }
    setValue([dates[0], dates[1]]);
  };

  const placeholderPair: [string, string] | undefined = placeholderOverride
    ? [`${placeholderOverride} from`, `${placeholderOverride} to`]
    : undefined;

  return (
    <RangePicker
      {...dr.controlProps}
      value={currentValue ?? undefined}
      onChange={(dates) => handleChange(dates as [Dayjs | null, Dayjs | null] | null)}
      size={size_}
      format={dr.format}
      minDate={dr.minDate}
      maxDate={dr.maxDate}
      disabledDate={dr.disabledDate}
      presets={dr.presets}
      allowEmpty={[true, true]}
      // Per-field clear via antd's native X, gated on `clearable`.
      allowClear={dr.clearable !== false}
      placeholder={placeholderPair}
    />
  );
};
